import { useState } from 'react'

import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import Button from '@space-core/components/button'
import Icon from '@space-core/components/icon'
import ModalBody from '@space-core/components/modalBody'
import ModalFooter from '@space-core/components/modalFooter'
import ModalHeader from '@space-core/components/modalHeader'
import Table from '@space-core/components/table'
import Tooltip from '@space-core/components/tooltip'
import useTranslation from '@space-core/hooks/useTranslation'
import { toastEventDispatch } from '@space-events/toast'
import { ToastType, ToastPosition } from '@space-events/toast/enum'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useDeleteFollower } from '@space-shell/modules/communities/hooks'

import { CustomModal } from './removeActionStyles'
import type { RemoveActionProps } from './types'

const RemoveAction = ({
  communityId,
  member,
  isTheOwner,
  canEdit,
  canDelete,
  isLoadingCommunityData,
  refetchGetFollowersRoles
}: RemoveActionProps) => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { deleteFollowerQuery } = useDeleteFollower()
  const hasNotPermissionToRemove = !canEdit || !canDelete || isTheOwner
  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleRemoveClick = () => {
    setIsModalOpen(true)
  }

  const {
    community: { isLocked }
  } = useCommunityConfigurationSidebar()
  const handleDeleteUser = () => {
    if (hasNotPermissionToRemove) return

    deleteFollowerQuery.mutate(
      { ucode: member.ucode, communityId },
      {
        onSuccess: () => {
          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: t('configuration_sidebar.members_and_permissions.messages.delete_success'),
            position: ToastPosition.BOTTOM_RIGHT
          })
          handleCloseModal()
          refetchGetFollowersRoles()
        },
        onError: () => {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: t('configuration_sidebar.members_and_permissions.messages.delete_error'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        }
      }
    )
  }

  return (
    <>
      <Table.Data className='_text-center' dataTest='remove-action'>
        <Tooltip
          content={t(
            `${isLocked ? 'configuration_sidebar.change_community_type_tooltip_alert' : ''}`
          )}
          position='top'>
          <Button
            data-test='remove-item-table'
            variation='tertiary'
            disabled={hasNotPermissionToRemove || isLocked}
            onClick={handleRemoveClick}>
            <Icon icon={faTrash} data-test='see-all-icon' />
          </Button>
        </Tooltip>
      </Table.Data>
      <CustomModal show={isModalOpen} onClose={handleCloseModal}>
        <ModalHeader className='_px-5 _pt-5 _pb-4'>
          <div className='_d-flex _align-items-baseline'>
            <p className='_text-4 _m-0 _mr-4'>
              {t('configuration_sidebar.members_and_permissions.list.delete_title', {
                memberName: member.name
              })}
            </p>
            <Button
              disabled={isLoadingCommunityData || deleteFollowerQuery.isLoading}
              variation='tertiary'
              className='_p-0 _d-flex _border-0 _bg-transparent _text-gray-400 _ml-auto'
              onClick={handleCloseModal}>
              <span className='_sr-only'>close</span>
              <Icon icon={faXmark} className='_w-5 _h-5' />
            </Button>
          </div>
        </ModalHeader>
        <ModalBody className='_p-0 _px-5'>
          <p className='_text-2 _text-gray-500 _m-0'>
            {t('configuration_sidebar.members_and_permissions.list.delete_description')}
          </p>
        </ModalBody>
        <ModalFooter className='_px-5 _pt-8 _pb-5'>
          <div className='_d-flex _ml-auto'>
            <Button
              disabled={isLoadingCommunityData || deleteFollowerQuery.isLoading}
              className='_mr-5'
              variation='default'
              onClick={handleCloseModal}>
              {t('buttons.cancel')}
            </Button>
            <Button
              variation='danger'
              onClick={handleDeleteUser}
              loading={isLoadingCommunityData || deleteFollowerQuery.isLoading}>
              <Icon icon={faTrash} className='_mr-2' />
              {t('configuration_sidebar.members_and_permissions.list.buttons.confirm_remove')}
            </Button>
          </div>
        </ModalFooter>
      </CustomModal>
    </>
  )
}

export default RemoveAction
